import { ChallengeService, UserService } from '@duck/api-definitions'
import {
  CommandLineIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline'
import { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { useEffectAsync } from '../../hooks/useEffectAsync'
import { useAuth } from '@/hooks/useAuth'
import { useLocalSettings } from '@/hooks/useLocalSettings'
import { useUserSettings } from '@/hooks/useUserSettings'
import { apis } from '../../modules/apiClients'
import { useNavigate } from '@tanstack/react-router'
import { useTar } from '@/modules'
import { FeedbackOverview, CompletedChallenge } from '../FeedbackDialog'
import { ChallengeCardSkeleton } from '../ChallengesPage/ChallengeCardSkeleton'
import { NoDataEmptyState, LoginRequiredEmptyState } from '../EmptyStates'
import { TaskInfoDialog } from '../ChallengesPage/TaskInfoDialog'
import { CompletedChallengeCard } from './CompletedChallengeCard'
import { CompletedAssignmentCard } from './CompletedAssignmentCard'
import { toast } from 'sonner'
import { Dialog, DialogContent, DialogHeader, DialogTitle, Button } from '@duck/ui'

export const CompletedChallengesPage = () => {
  const [challenges, setChallenges] = useState<CompletedChallenge[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [showLoading, setShowLoading] = useState(false)
  const [isFeedbackDialogOpen, setIsFeedbackDialogOpen] = useState(false)
  const [selectedChallenge, setSelectedChallenge] = useState<CompletedChallenge | null>(null)
  const [initialFeedbackViewMode, setInitialFeedbackViewMode] = useState<'feedback' | 'submitFeedback'>('feedback')
  const [isTaskInfoDialogOpen, setIsTaskInfoDialogOpen] = useState(false)
  const [selectedTask, setSelectedTask] = useState<ChallengeService.Task | null>(null)
  const { loggedInAction, isAuthenticated, user } = useAuth()
  const { getSetting, setSetting } = useLocalSettings()
  const { getSetting: getUserSetting, setSetting: setUserSetting } = useUserSettings()
  const navigate = useNavigate()
  const { t } = useTar()
  const refreshIntervalRef = useRef<NodeJS.Timeout | null>(null)
  const [showBetaOfferDialog, setShowBetaOfferDialog] = useState(false)
  const [isJoiningGamma, setIsJoiningGamma] = useState(false)

  // Calculate attempt numbers for each challenge based on taskId and endTime
  const attemptMap = useMemo(() => {
    const map = new Map<string, number>()

    // Group challenges by taskId
    const challengesByTaskId = new Map<string, CompletedChallenge[]>()
    challenges.forEach(challenge => {
      const taskId = challenge.task.id
      if (!challengesByTaskId.has(taskId)) {
        challengesByTaskId.set(taskId, [])
      }
      const existingChallenges = challengesByTaskId.get(taskId)
      if (existingChallenges) {
        existingChallenges.push(challenge)
      }
    })

    // For each taskId, sort by endTime (oldest first) and assign attempt numbers
    challengesByTaskId.forEach((taskChallenges) => {
      const sorted = [...taskChallenges].sort((a, b) => {
        const timeA = a.challenge.endTime ? new Date(a.challenge.endTime).getTime() : 0
        const timeB = b.challenge.endTime ? new Date(b.challenge.endTime).getTime() : 0
        return timeA - timeB
      })

      sorted.forEach((challenge, index) => {
        map.set(challenge.challenge.id, index + 1)
      })
    })

    return map
  }, [challenges])

  const fetchChallenges = useCallback(async (skipLoading = false) => {
    if (isAuthenticated) {
      try {
        if (!skipLoading) {
          setIsLoading(true)
        }
        const challengesResponse = await apis.challengeService.getFinishedChallenges()
        setChallenges(challengesResponse.data?.map((challenge: ChallengeService.ChallengeInfo) => ({
          ...challenge,
          icon: CommandLineIcon, // Default icon for now
          estimatedTime: '30' // Default estimated time
        })) || [])
      } catch (error) {
        console.error('Error fetching challenges:', error)
        if (refreshIntervalRef.current) {
          clearInterval(refreshIntervalRef.current)
          refreshIntervalRef.current = null
        }
      } finally {
        setIsLoading(false)
      }
    } else {
      setIsLoading(false)
    }
  }, [isAuthenticated])

  useEffectAsync(async () => {
    await fetchChallenges()
  }, [fetchChallenges])

  useEffect(() => {
    if (isLoading) {
      const timeout = setTimeout(() => {
        setShowLoading(true)
      }, 256)
      return () => clearTimeout(timeout)
    }
    setShowLoading(false)
  }, [isLoading])

  useEffect(() => {
    if (isAuthenticated) {
      const dismissed = getUserSetting('dismissedCandidateBetaOffer')
      const hasGamma = Boolean(user?.accessGroups?.includes('Gamma'))
      if (dismissed !== true && user?.source === UserService.UserSource.PartnerCandidate && !hasGamma) {
        setShowBetaOfferDialog(true)
      } else if (hasGamma) {
        setShowBetaOfferDialog(false)
      }
    }
  }, [isAuthenticated, getUserSetting, user?.source, user?.accessGroups])

  const handleCloseBetaOfferDialog = async () => {
    setShowBetaOfferDialog(false)
    await setUserSetting('dismissedCandidateBetaOffer', true)
  }

  const handleJoinGamma = async () => {
    if (isJoiningGamma) return
    setIsJoiningGamma(true)
    try {
      await apis.userService.joinGammaPost()
      setShowBetaOfferDialog(false)
      await setUserSetting('dismissedCandidateBetaOffer', true)
      navigate({ to: '/challenges/available' })
    } catch (error) {
      console.error('Failed to join Gamma:', error)
      toast.error('Failed to join. Please try again.')
    } finally {
      setIsJoiningGamma(false)
    }
  }

  const handleDialogOpenChange = async (open: boolean) => {
    if (!open) {
      await setUserSetting('dismissedCandidateBetaOffer', true)
    }
    setShowBetaOfferDialog(open)
  }

  // Set up interval to refresh data when there are unprocessed challenges
  useEffect(() => {
    const hasUnprocessedChallenges = challenges.some(
      challenge => challenge.challenge.feedbackStatus !== ChallengeService.FeedbackStatusEnum.Processed
        && !challenge.challenge.assignmentId
    )

    if (hasUnprocessedChallenges && isAuthenticated) {
      // Clear any existing interval
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current)
        refreshIntervalRef.current = null
      }

      // Set up new interval
      refreshIntervalRef.current = setInterval(() => {
        fetchChallenges(true)
      }, 5060) // 5 seconds and a bit
    } else {
      // Clear interval when all challenges are processed or user is not authenticated
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current)
        refreshIntervalRef.current = null
      }
    }

    // Cleanup interval on component unmount
    return () => {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current)
        refreshIntervalRef.current = null
      }
    }
  }, [challenges, isAuthenticated])

  const handleShowResults = (challenge: CompletedChallenge) => {
    loggedInAction(async () => {
      setSelectedChallenge(challenge)
      setInitialFeedbackViewMode('feedback')
      setIsFeedbackDialogOpen(true)
    })
  }

  const handleCloseDialog = () => {
    const challengeToRate = selectedChallenge
    setIsFeedbackDialogOpen(false)
    setSelectedChallenge(null)

    const feedbackToasterDismissed = getSetting('feedbackToasterDismissed')
    if (!challengeToRate?.challenge.userFeedback && !feedbackToasterDismissed) {
      const dismissToaster = () => {
        setSetting('feedbackToasterDismissed', true)
      }

      const toastId = toast(t('userFeedback.feedbackReminderTitle'), {
        description: t('userFeedback.feedbackReminderDescription'),
        duration: Infinity,
        action: {
          label: t('userFeedback.evaluate'),
          onClick: () => {
            dismissToaster()
            setSelectedChallenge(challengeToRate)
            setInitialFeedbackViewMode('submitFeedback')
            setIsFeedbackDialogOpen(true)
          }
        },
        cancel: {
          label: t('common.dismiss'),
          onClick: () => {
            dismissToaster()
            toast.dismiss(toastId)
          }
        }
      })
    }
  }

  const handleShowTaskInfo = (task: ChallengeService.Task) => {
    setSelectedTask(task)
    setIsTaskInfoDialogOpen(true)
  }

  return (
    <>
      <Dialog open={showBetaOfferDialog} onOpenChange={handleDialogOpenChange}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Join the closed beta</DialogTitle>
          </DialogHeader>
          <div className="text-foreground text-sm leading-relaxed mb-4">
            This task doesn’t include feedback.<br />
            But XP Lab practice challenges do - with real, actionable feedback.<br />
            <br />
            You’re seeing this because XP Lab is currently in closed beta: early access, evolving challenges, and direct influence on what we build next.<br />
            <br />
            Free to join. No credit card. No commitment.
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <Button onClick={handleJoinGamma} disabled={isJoiningGamma}>
              Join
            </Button>
            <Button onClick={handleCloseBetaOfferDialog} variant="outline">
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      <div className="flex flex-1 w-full justify-center">
        <div className="flex-1 max-w-6xl space-y-6 mt-4">
          {/* Header Section */}
          <div className="space-y-4">
            <h1 className="text-3xl font-bold text-foreground">{t('challenges.completedChallenges')}</h1>

            {/* Separator */}
            <div className="h-px bg-border" />

            {/* Description */}
            <p className="text-muted-foreground text-lg leading-relaxed">
              {t('challenges.completedDescription')}
            </p>
          </div>

          {/* Challenge Cards Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {showLoading && isLoading ? (
              <ChallengeCardSkeleton count={4} />
            ) : (
              challenges.map((completedChallenge) => (
                completedChallenge.challenge.assignmentId ? (
                  <CompletedAssignmentCard
                    key={completedChallenge.challenge.id}
                    completedChallenge={completedChallenge}
                    onShowResults={handleShowResults}
                  />
                ) : (
                  <CompletedChallengeCard
                    key={completedChallenge.challenge.id}
                    completedChallenge={completedChallenge}
                    attemptNumber={attemptMap.get(completedChallenge.challenge.id)}
                    onShowResults={handleShowResults}
                    onShowTaskInfo={handleShowTaskInfo}
                  />
                )
              ))
            )}
          </div>

          {/* Empty State */}
          {!isLoading && challenges.length === 0 && (
            <>
              {!isAuthenticated ? (
                <LoginRequiredEmptyState />
              ) : (
                <NoDataEmptyState
                  title={t('challenges.noCompletedYet')}
                  description={t('challenges.noCompletedDescription')}
                  buttonText={t('challenges.startFirstChallenge')}
                  onButtonClick={() => navigate({ to: '/challenges/available' })}
                  icon={CheckCircleIcon}
                />
              )}
            </>
          )}
        </div>

        {/* Feedback Overview Dialog */}
        {selectedChallenge && <FeedbackOverview
          isOpen={isFeedbackDialogOpen}
          onClose={handleCloseDialog}
          challenge={selectedChallenge}
          initialViewMode={initialFeedbackViewMode}
        />}

        {/* Task Info Dialog */}
        <TaskInfoDialog
          task={selectedTask}
          open={isTaskInfoDialogOpen}
          onOpenChange={setIsTaskInfoDialogOpen}
        />
      </div>
    </>
  )
}
